import { enums } from "@shared/constants";
import { SeatingLocationMapIdType } from "@shared/constants/enums/enums-types";
import Konva from "konva";
import SeatingService from "../services/SeatingService";
import {
  PublicBaseLocationSerializer,
  TransformType,
  PointType,
} from "../types/seating-types";
import GeneralAdmissionFactory from "./GeneralAdmissionFactory";
import RowFactory from "./RowFactory";
import SeatFactory from "./SeatFactory";
import SeatingUtilsFactory from "./SeatingUtilsFactory";

type BuildersType =
  | RowFactory["create"]
  | RowFactory["createSquareTable"]
  | RowFactory["createRoundTable"]
  | RowFactory["createSemiRoundTable"]
  | GeneralAdmissionFactory["create"]
  | (() => { groupActive?: Konva.Group; groupDisabled?: Konva.Group });

export default class LocationFactory {
  public rowFactory: RowFactory;

  public generalAdmissionFactory: GeneralAdmissionFactory;

  constructor(
    seatingService: SeatingService,
    generalAdmissionFactory: GeneralAdmissionFactory,
    seatFactory: SeatFactory
  ) {
    this.rowFactory = new RowFactory(seatingService, seatFactory);
    this.generalAdmissionFactory = generalAdmissionFactory;
  }

  getBuilder(type: SeatingLocationMapIdType): BuildersType {
    const locationTypes = enums.SEATING_LOCATION_TYPE_MAP;
    const builders: { [key: string]: BuildersType } = {
      [locationTypes.TYPE_ROW.id]: this.rowFactory.create,
      [locationTypes.TYPE_SQUARE_TABLE.id]: this.rowFactory.createSquareTable,
      [locationTypes.TYPE_TABLE.id]: this.rowFactory.createRoundTable,
      [locationTypes.TYPE_SEMI_ROUND_TABLE.id]:
        this.rowFactory.createSemiRoundTable,
      [locationTypes.TYPE_GENERAL_ADMISSION.id]:
        this.generalAdmissionFactory.create,
      default: () => {
        return { groupActive: undefined, groupDisabled: undefined };
      },
    };

    return builders[type] || builders.default;
  }

  private static readonly FALLBACK_LOCATION_BG = "#f4f4f4";

  static readonly LOCATION_BG_NODE_NAME = "location-bg";

  /**
   * Resolves the location background color from the current Chakra theme.
   * Reads the CSS variable at call time so it adapts to branding and dark mode.
   */
  static resolveLocationBackgroundColor(): string {
    if (typeof document === "undefined") {
      return LocationFactory.FALLBACK_LOCATION_BG;
    }
    const resolved = getComputedStyle(document.documentElement)
      .getPropertyValue("--chakra-colors-seating-location-background")
      .trim();

    return resolved || LocationFactory.FALLBACK_LOCATION_BG;
  }

  create(
    locationSet: PublicBaseLocationSerializer[],
    display: { points: PointType[] },
    identifier: string,
    { x = 0, y = 0, rotation = 0 }: TransformType,
    seatSize: number,
    seatSpacing: number,
    hideLocationNames: boolean,
    hideSegmentBackground: boolean
  ): {
    locationGroupActive: Konva.Group;
    locationGroupDisabled: Konva.Group;
  } {
    const container = new Konva.Line({
      points: display.points.flatMap((point: number[]) => point),
      fill: LocationFactory.resolveLocationBackgroundColor(),
      name: LocationFactory.LOCATION_BG_NODE_NAME,
      closed: true,
      perfectDrawEnabled: false,
      listening: false,
      opacity: hideSegmentBackground ? 0 : 1,
    });

    const locationGroupActive = new Konva.Group({
      x,
      y,
      draggable: false,
      name: "location",
      identifier,
    });

    const locationGroupDisabled = new Konva.Group({
      x,
      y,
      draggable: false,
      name: "location_disabled",
      identifier,
    });

    locationGroupDisabled.add(container);

    // Calculate uniform font size for GA locations to ensure consistency
    const gaLocations = locationSet?.filter(
      (loc) =>
        loc.type === enums.SEATING_LOCATION_TYPE_MAP.TYPE_GENERAL_ADMISSION.id
    );
    const uniformFontSize =
      gaLocations && gaLocations.length > 0
        ? GeneralAdmissionFactory.calculateUniformFontSize(gaLocations)
        : undefined;

    // based on the locationData input, create the location based on its type
    // this could be a row, table, GA location, etc
    locationSet?.forEach((locationData) => {
      const builder = this.getBuilder(locationData.type);
      const params = {
        location: locationData,
        transform: {
          x: locationData.transform.x + x,
          y: locationData.transform.y + y,
          rotation: Number(locationData.transform.rotation),
        },
        seatSize,
        seatSpacing,
        segmentTransform: { x, y, rotation: Number(rotation) },
        hideLocationNames,
        uniformFontSize,
      };

      const location = builder(params);

      // if the location is active, add it to the active group
      if (location?.groupActive?.hasChildren()) {
        locationGroupActive.add(location.groupActive);
      }
      // if the location is disabled, add it to the disabled group
      if (location?.groupDisabled?.hasChildren()) {
        locationGroupDisabled.add(location.groupDisabled);
      }
    });

    SeatingUtilsFactory.rotateAroundCenter(locationGroupActive, rotation ?? 0);
    SeatingUtilsFactory.rotateAroundCenter(
      locationGroupDisabled,
      rotation ?? 0
    );

    const groupActiveBounds = locationGroupActive.getClientRect();
    locationGroupActive.width(groupActiveBounds.width);
    locationGroupActive.height(groupActiveBounds.height);
    locationGroupActive.setAttr("visibleGroupOffset", {
      x: groupActiveBounds.x - x,
      y: groupActiveBounds.y - y,
    });

    const groupDisabledBounds = locationGroupDisabled.getClientRect();
    locationGroupDisabled.width(groupDisabledBounds.width);
    locationGroupDisabled.height(groupDisabledBounds.height);
    locationGroupDisabled.setAttr("visibleGroupOffset", {
      x: groupDisabledBounds.x - x,
      y: groupDisabledBounds.y - y,
    });

    return { locationGroupActive, locationGroupDisabled };
  }
}
